import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { ProdutoListStruct } from '../../../../../shared/services/structs/produto/produto-list.struct';
import { TipoCustoKitFabricadoEnum } from '../../../../../shared/enums/produto/tipo-custo-kit-fabricado.enum';
import { ConfiguracaoProduto } from '../../../../../shared/services/models/produto/configuracao-produto.model';
import { TipoProdutoEnum } from '../../../../../shared/enums/produto/tipo-produto.enum';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ProdutoComposicaoKitStruct } from '../../../../../shared/services/structs/produto/produto-composicao-kit.struct';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { UtilService } from '../../../../../shared/services/util.service';
import { AuthService } from '../../../../../shared/services/apis/usuario/auth.service';

@Component({
  selector: 'app-produto-kits',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    NgxCurrencyDirective,
    SelectPesquisavelComponent,
    AbntPipe,
    EmptyStateComponent,
    AlertCardComponent,
  ],
  templateUrl: './produto-kits.component.html',
  styleUrl: './produto-kits.component.css',
  providers: [AbntPipe],
})
export class ProdutoKitsComponent implements OnInit {
  @Input({ required: true }) form!: FormGroup;
  @Input() urlProduto: string = '';
  @Input() quantidadeMask!: any;
  @Input() defaultCurrencyMask!: any;
  @Input({ required: true }) configuracaoProduto!: ConfiguracaoProduto;
  // Criamos um Input específico para dados de inicialização
  @Input() set dadosIniciais(composicao: ProdutoComposicaoKitStruct[]) {
    if (composicao && composicao.length > 0) {
      this.limparEPreencherGrade(composicao);
    }
  }
  // Eventos de alto nível para o pai
  @Output() precoAtualizado = new EventEmitter<number>();
  @Output() pesoAtualizado = new EventEmitter<{ pesoBruto: number; pesoLiquido: number }>();
  /** Avisa o pai quando há componente com quantidade fora do múltiplo (bloqueia a gravação). */
  @Output() multiploInvalido = new EventEmitter<boolean>();

  // Totals for Kit
  public totalQuantidadeKit = 0;
  public totalPrecoVendaKit = 0;
  public totalCustoMedioKit = 0;
  public totalPrecoGeralKit = 0;

  public _tipoProdutoEnum = TipoProdutoEnum;

  /** Tolerância de arredondamento ao checar se a quantidade é múltipla do fator (RN05). */
  private static readonly toleranciaMultiplo = 0.0001;

  /** Último valor emitido em multiploInvalido, para não reemitir o mesmo estado. */
  private multiploInvalidoAtual = false;

  /** Casas decimais de quantidade configuradas pela empresa — o fator do múltiplo é quantidade. */
  private get casasDecimaisQuantidade(): number {
    return this.authService.getUserInfos()?.casasDecimaisQuantidade || 2;
  }

  /** Subject para consolidar múltiplos updateKitTotals em um único emit */
  private precoDebounce$ = new Subject<number>();

  constructor(
    private fb: FormBuilder,
    private alertService: AlertService,
    private authService: AuthService,
    private abntPipe: AbntPipe
  ) {}

  ngOnInit(): void {
    // Atualizar totais na inicialização se já houver dados
    this.updateKitTotals();

    // Debounce para evitar chamadas API duplicadas
    this.precoDebounce$.pipe(debounceTime(300)).subscribe(preco => {
      this.precoAtualizado.emit(preco);
    });

    // Recalcula sempre que o toggle "Preço Fixo" ou o preço manual do kit mudam
    this.form.get('isPrecoKitFixo')?.valueChanges.subscribe(() => this.updateKitTotals());
    this.form.get('precoKitFixo')?.valueChanges.subscribe(() => this.updateKitTotals());
  }

  get composicao(): FormArray {
    return this.form.get('composicao') as FormArray;
  }

  get isPrecoKitFixo(): boolean {
    return this.form.get('isPrecoKitFixo')?.value ?? true;
  }

  get precoFixoHint(): string {
    return this.isPrecoKitFixo
      ? 'Informe o preço de venda do kit — o valor será rateado proporcionalmente entre os itens.'
      : 'O preço de venda acompanha automaticamente a soma dos itens do kit.';
  }

  getControl(item: any, controlName: string): FormControl {
    return item.get(controlName) as FormControl;
  }

  private limparEPreencherGrade(dados: any[]) {
    this.composicao.clear(); // Limpa o array atual
    dados.forEach(item => {
      // Usa a própria lógica interna de criação de row do filho
      this.addComposicaoRow(item);
    });
  }

  /**
   * ========================================
   * MÉTODOS INTERNOS DE MANIPULAÇÃO DE DADOS
   * ========================================
   */

  addComposicaoRow(data?: any): void {
    const produtoList: ProdutoListStruct = data?.produtoListStruct;

    const group = this.fb.group({
      idProdutoFilho: [data?.idProdutoFilho, Validators.required],
      quantidade: [data?.quantidade || 1],
      // Hidden fields for weight calculation
      pesoBrutoKg: [data?.produtoListStruct?.pesoBrutoKg || 0],
      pesoLiquidoKg: [data?.produtoListStruct?.pesoLiquidoKg || 0],
      // UI fields
      nomeProdutoFilho: [{ value: data?.nomeProdutoFilho || produtoList?.descricao || '', disabled: true }],
      skuProdutoFilho: [{ value: data?.skuProdutoFilho || produtoList?.codigoSku || '', disabled: true }],
      precoVenda: [{ value: data?.precoVenda || produtoList?.preco || 0, disabled: true }],
      precoCusto: [{ value: data?.precoCusto || produtoList?.custo || 0, disabled: true }],
      custoMedio: [{ value: data?.custoMedio || produtoList?.custoMedio || 0, disabled: true }],
      precoTotal: [
        { value: (data?.quantidade || 1) * (data?.precoVenda || produtoList?.preco || 0), disabled: true },
      ],
      // Múltiplo do componente — vem do cadastro do produto, nunca é editável dentro do kit (RN08).
      isControlaMultiplo: [{ value: produtoList?.isControlaMultiplo ?? false, disabled: true }],
      fatorMultiplo: [{ value: produtoList?.fatorMultiplo ?? null, disabled: true }],
      unidadeMultiplo: [{ value: produtoList?.unidadeMultiplo ?? null, disabled: true }],
      // Helper for Select Component Preload
      preloadedList: [produtoList ? [produtoList] : []],
    });

    // Update total when quantity changes
    group.get('quantidade')?.valueChanges.subscribe(qtd => {
      const preco = group.get('precoVenda')?.value || 0;
      group.get('precoTotal')?.setValue(qtd * preco);
    });

    group.valueChanges.subscribe(() => {
      this.updateKitTotals();
    });

    this.composicao.push(group);
    this.updateKitTotals();
  }

  removeComposicaoRow(index: number): void {
    this.composicao.removeAt(index);
    this.updateKitTotals();
  }

  onProdutoKitSelected(event: any, index: number): void {
    if (!event) return;
    const group = this.composicao.at(index) as FormGroup;

    group.patchValue({
      nomeProdutoFilho: event.descricao,
      skuProdutoFilho: event.codigoSku || event.sku,
      precoVenda: event.precoVenda || event.preco || 0,
      precoCusto: event.precoCusto || event.custo || 0,
      custoMedio: event.custoMedio || 0,
      // Update weights
      pesoBrutoKg: event.pesoBrutoKg || 0,
      pesoLiquidoKg: event.pesoLiquidoKg || 0,
      // Múltiplo herdado do cadastro do componente (RN08)
      isControlaMultiplo: event.isControlaMultiplo ?? false,
      fatorMultiplo: event.fatorMultiplo ?? null,
      unidadeMultiplo: event.unidadeMultiplo ?? null,
    });

    // Recalculate total
    const qtd = group.get('quantidade')?.value || 0;
    const preco = group.get('precoVenda')?.value || 0;
    group.get('precoTotal')?.setValue(qtd * preco);

    this.updateKitTotals();
  }

  /**
   * ========================================
   * CÁLCULOS INTERNOS
   * ========================================
   */

  /**
   * Soma dos preços unitários originais (base, sem rateio) × quantidade de cada item.
   * É a referência para o rateio: quando "Preço Fixo" está ativo, o valor informado em
   * "Preço de venda no kit" é distribuído proporcionalmente a essa soma.
   */
  private getSomaBaseKit(items: any[]): number {
    return items.reduce((acc: number, curr: any) => acc + (curr.quantidade || 0) * (curr.precoVenda || 0), 0);
  }

  /** Fator de rateio: 1 quando o preço não é fixo (cada item mantém seu preço base). */
  public getRateioKit(): number {
    if (!this.isPrecoKitFixo) return 1;
    const somaBase = this.getSomaBaseKit(this.composicao.getRawValue());
    if (somaBase <= 0) return 1;
    const precoManual = this.form.get('precoKitFixo')?.value;
    const precoAlvo = precoManual != null ? precoManual : somaBase;
    return precoAlvo / somaBase;
  }

  /** Valor unitário do item já rateado — o que efetivamente "vale" dentro do kit. */
  public getUnitarioNoKit(item: any): number {
    return (item.get('precoVenda')?.value || 0) * this.getRateioKit();
  }

  /** Valor total do item (unitário rateado × quantidade). */
  public getTotalNoKit(item: any): number {
    return this.getUnitarioNoKit(item) * (item.get('quantidade')?.value || 0);
  }

  private updateKitTotals(): void {
    const items = this.composicao.getRawValue();
    this.totalQuantidadeKit = items.reduce((acc: number, curr: any) => acc + (curr.quantidade || 0), 0);
    this.totalCustoMedioKit = items.reduce((acc: number, curr: any) => acc + (curr.custoMedio || 0), 0);

    const somaBase = this.getSomaBaseKit(items);
    const precoManual = this.form.get('precoKitFixo')?.value;
    const totalGeral = this.isPrecoKitFixo ? (precoManual != null ? precoManual : somaBase) : somaBase;

    this.totalPrecoVendaKit = totalGeral;
    this.totalPrecoGeralKit = totalGeral;

    // Propaga o preço de venda do kit para a lista padrão (rateado ou pela composição).
    this.precoDebounce$.next(totalGeral);

    this.emitirMultiploInvalido();
  }

  /**
   * O pai usa isso para desabilitar o Salvar enquanto houver quantidade fora do múltiplo (RN06).
   * Emite só na virada e num microtask: updateKitTotals roda dentro do ngOnInit, e emitir de forma
   * síncrona ali mudaria o [isSalvarDisabled] do pai no meio do ciclo de detecção de mudanças
   * (ExpressionChangedAfterItHasBeenCheckedError em dev).
   */
  private emitirMultiploInvalido(): void {
    const invalido = this.componentesForaDoMultiplo > 0;
    if (invalido === this.multiploInvalidoAtual) return;

    this.multiploInvalidoAtual = invalido;
    Promise.resolve().then(() => this.multiploInvalido.emit(invalido));
  }

  atualizarPesoKit(): void {
    const items = this.composicao.getRawValue();
    const totalPesoBruto = items.reduce((acc: number, curr: any) => {
      return acc + (curr.pesoBrutoKg || 0) * (curr.quantidade || 0);
    }, 0);
    const totalPesoLiquido = items.reduce((acc: number, curr: any) => {
      return acc + (curr.pesoLiquidoKg || 0) * (curr.quantidade || 0);
    }, 0);

    // Emite os pesos calculados para o pai atualizar
    this.pesoAtualizado.emit({ pesoBruto: totalPesoBruto, pesoLiquido: totalPesoLiquido });
    this.alertService.show('Sucesso', 'Pesos calculados e enviados para atualização!', AlertType.success);
  }

  /**
   * ========================================
   * HELPERS DE VISUALIZAÇÃO
   * ========================================
   */
  public getCustoLabel(precoCusto: number, custoMedio: number): string {
    if (!this.configuracaoProduto) {
      return `R$ ${precoCusto?.toFixed(2) || '0,00'} (Custo)`;
    }

    const tipoCusto = this.configuracaoProduto.idTipoCustoKitFabricado;
    if (tipoCusto === TipoCustoKitFabricadoEnum.CustoMedio) {
      return `R$ ${custoMedio?.toFixed(2) || '0,00'} (Custo)`;
    }
    return `R$ ${precoCusto?.toFixed(2) || '0,00'} (Custo)`;
  }

  // ── Controle de múltiplo dos componentes ────────────────────────────────────

  /**
   * Avalia a quantidade de um item contra o múltiplo configurado no cadastro do componente (RN05).
   * Componente sem múltiplo não é avaliado — a quantidade é livre.
   */
  public getMultiploStatus(item: any): { semMultiplo: boolean; valido: boolean } {
    const isControlaMultiplo = item.get('isControlaMultiplo')?.value === true;
    const fator = Number(item.get('fatorMultiplo')?.value ?? 0);

    if (!isControlaMultiplo || !(fator > 0)) {
      return { semMultiplo: true, valido: true };
    }

    const casas = this.casasDecimaisQuantidade;
    const fatorArredondado = UtilService.arredondarAbnt(fator, casas);
    const quantidade = UtilService.arredondarAbnt(Number(item.get('quantidade')?.value ?? 0), casas);

    // A tolerância vale para os dois lados: o resto pode estar logo ACIMA de um múltiplo (resto ~ 0)
    // ou logo ABAIXO do próximo (resto ~ fator). Espelha ValidarMultiploComposicaoKit no backend.
    const tolerancia = ProdutoKitsComponent.toleranciaMultiplo;
    const resto = quantidade % fatorArredondado;
    const valido = quantidade > 0 && (resto <= tolerancia || fatorArredondado - resto <= tolerancia);

    return { semMultiplo: false, valido };
  }

  /** Quantas embalagens a quantidade informada representa — o "= 2,00 CX" do indicador verde. */
  public getQuantidadeEmMultiplos(item: any): number {
    const fator = Number(item.get('fatorMultiplo')?.value ?? 0);
    if (!(fator > 0)) return 0;
    return Number(item.get('quantidade')?.value ?? 0) / fator;
  }

  /** Componentes cuja quantidade está fora do múltiplo — alimenta o alerta e o bloqueio do Salvar. */
  public get componentesForaDoMultiplo(): number {
    return this.composicao.controls.filter(item => {
      const status = this.getMultiploStatus(item);
      return !status.semMultiplo && !status.valido;
    }).length;
  }

  /** Mensagem do alerta agregado ao final da lista de componentes (RN06). */
  public get alertaMultiploTexto(): string {
    const invalidos = this.composicao.controls.filter(item => {
      const status = this.getMultiploStatus(item);
      return !status.semMultiplo && !status.valido;
    });

    if (invalidos.length === 1) {
      const item = invalidos[0];
      // Mesmo pipe do indicador inline, para as duas mensagens formatarem o fator igual.
      const fator = this.abntPipe.transform(item.get('fatorMultiplo')?.value, 'quantidade');
      return `${item.get('nomeProdutoFilho')?.value} é vendido em ${item.get('unidadeMultiplo')?.value} de ${fator}. Informe uma quantidade múltipla de ${fator}.`;
    }

    return `${invalidos.length} componentes estão com quantidade fora do múltiplo configurado no cadastro. Ajuste as quantidades para gravar o kit.`;
  }

  public getCustoTotalLabel(total: number): string {
    if (!this.configuracaoProduto) {
      return `R$ ${total?.toFixed(2) || '0,00'} (Custo)`;
    }

    const tipoCusto = this.configuracaoProduto.idTipoCustoKitFabricado;
    const label = tipoCusto === TipoCustoKitFabricadoEnum.CustoMedio ? 'Custo Médio' : 'Custo';
    return `R$ ${total?.toFixed(2) || '0,00'} (${label})`;
  }
}
